"use client";

import * as React from "react";
import { twMerge } from "tailwind-merge";
import { SECTION_NAVIGATE_EVENT } from "../Section/Section";

/** A section is the one on screen once its top passes this far below the top of the scroll viewport. */
const ACTIVE_LINE_PX = 96;

/**
 * SectionNav — In-page navigation for content sections with smooth scrolling
 *
 * Provides sticky sidebar navigation on desktop and horizontal scrollable tabs on mobile.
 * Automatically tracks the active section based on scroll position and enables smooth
 * scrolling to sections when clicked.
 *
 * ## Features
 * - Responsive: Sidebar on desktop (lg+), horizontal tabs on mobile
 * - Sticky positioning for persistent visibility
 * - Smooth scroll to sections
 * - Active state management
 * - Customizable styling and positioning
 *
 * @example
 * ```tsx
 * <SectionNav
 *   sections={[
 *     { id: 'overview', label: 'Overview' },
 *     { id: 'features', label: 'Features' },
 *     { id: 'pricing', label: 'Pricing' }
 *   ]}
 *   activeSection="overview"
 *   onSectionChange={(id) => console.log('Active section:', id)}
 * />
 * ```
 */

export type SectionNavItem = {
  /** Unique identifier that matches the section's HTML id */
  id: string;
  /** Display label for the navigation item */
  label: string;
  /** Optional count badge to display next to the label */
  count?: number;
};

export type SectionNavOrientation = "vertical" | "horizontal" | "responsive";

export type SectionNavProps = {
  /** Array of section items to navigate between */
  sections: SectionNavItem[];
  /** Currently active section id */
  activeSection?: string | null;
  /** Callback when active section changes (controlled mode) */
  onSectionChange?: (sectionId: string) => void;
  /** Navigation orientation: vertical (sidebar), horizontal (tabs), or responsive (auto) */
  orientation?: SectionNavOrientation;
  /** Additional CSS classes for the nav container */
  className?: string;
  /** Custom scroll behavior options */
  scrollBehavior?: ScrollBehavior;
  /** Top offset for sticky positioning (default: 24px / 1.5rem) */
  stickyTop?: string;
  /** Optional heading shown above the vertical nav items (e.g. "On this page"). */
  heading?: string;
  /** ID prefix for section elements (default: 'section-') */
  sectionIdPrefix?: string;
  /** Custom filter function to determine which sections are visible */
  filterSection?: (section: SectionNavItem) => boolean;
  /**
   * ID of the scrollable container element. When set, scroll-to and
   * scroll tracking use this element instead of the window/viewport.
   * This is required when the page content scrolls inside an inner container
   * (e.g. AppShell's #app-content) rather than the document itself.
   * @default undefined (uses window)
   */
  scrollContainerId?: string;
};

export function SectionNav({
  sections,
  activeSection: controlledActiveSection,
  onSectionChange,
  orientation = "responsive",
  className,
  scrollBehavior = "smooth",
  stickyTop = "1.5rem",
  heading,
  sectionIdPrefix = "section-",
  filterSection,
  scrollContainerId,
}: SectionNavProps) {
  const verticalNavRef = React.useRef<HTMLElement>(null);
  const horizontalNavRef = React.useRef<HTMLElement>(null);
  // A clicked section whose scroll was clamped at the bottom stays active there until the reader scrolls off it.
  const clampedClickRef = React.useRef<{ id: string; reachedBottom: boolean } | null>(null);
  const [internalActiveSection, setInternalActiveSection] = React.useState<string | null>(sections[0]?.id || null);

  // Use controlled or uncontrolled active section
  const activeSection = controlledActiveSection ?? internalActiveSection;
  const activeSectionRef = React.useRef(activeSection);
  React.useLayoutEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  // Resolve the scroll container element (inner container or window fallback)
  const getScrollContainer = React.useCallback((): HTMLElement | null => {
    if (scrollContainerId) {
      return document.getElementById(scrollContainerId);
    }
    return null; // null = use window
  }, [scrollContainerId]);

  // Prefix tolerance (MR-D3 Layer 3 / D9): resolve a section's DOM element whether
  // the page emitted it under the prefixed id (`${sectionIdPrefix}<id>`, the norm)
  // or a bare `<id>`. Prefixed wins when both exist. This is what lets legacy deep
  // links in either form actually scroll instead of silently doing nothing.
  const resolveSectionElement = React.useCallback(
    (id: string): HTMLElement | null =>
      document.getElementById(`${sectionIdPrefix}${id}`) ?? document.getElementById(id),
    [sectionIdPrefix]
  );

  // On mount, if the URL has a hash matching a section, scroll to it and set it active
  React.useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const targetId = hash.slice(1); // remove '#'
    // Prefix tolerance (D9): accept both `#${sectionIdPrefix}<id>` and a bare `#<id>`.
    const bareId = targetId.startsWith(sectionIdPrefix) ? targetId.slice(sectionIdPrefix.length) : targetId;
    const matchingSection = sections.find((s) => s.id === bareId);
    if (!matchingSection) return;

    // Defer to next frame so the DOM is fully rendered
    requestAnimationFrame(() => {
      const element = resolveSectionElement(matchingSection.id);
      if (!element) return;

      const scrollContainer = getScrollContainer();
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const targetScroll = scrollContainer.scrollTop + (elementRect.top - containerRect.top);
        scrollContainer.scrollTo({ top: Math.max(0, targetScroll), behavior: "instant" });
      } else {
        element.scrollIntoView({ behavior: "instant", block: "start" });
      }

      if (onSectionChange) {
        onSectionChange(matchingSection.id);
      } else {
        setInternalActiveSection(matchingSection.id);
      }
    });
    // Only run on mount
  }, []);

  // Track scroll position to update active section
  React.useEffect(() => {
    // If fully controlled (has both activeSection and onSectionChange), skip auto-tracking
    if (controlledActiveSection !== undefined && onSectionChange) {
      return;
    }

    // Check if a nav element is actually visible (not hidden by CSS)
    // This handles cases where multiple SectionNav instances are rendered but hidden with CSS
    const isNavVisible = (navEl: HTMLElement | null) => {
      if (!navEl) return false;
      const style = window.getComputedStyle(navEl);
      return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
    };

    // Wait a tick to ensure DOM is ready and CSS is applied
    const checkAndObserve = () => {
      // Check which nav (vertical or horizontal) is currently visible
      const visibleVertical = isNavVisible(verticalNavRef.current);
      const visibleHorizontal = isNavVisible(horizontalNavRef.current);

      if (!visibleVertical && !visibleHorizontal) {
        return null;
      }

      const scrollContainer = getScrollContainer();

      const activate = (id: string) => {
        if (id === activeSectionRef.current) return;
        activeSectionRef.current = id;
        if (onSectionChange) {
          onSectionChange(id);
        } else {
          setInternalActiveSection(id);
        }
      };

      // A short last section never reaches the active line, so the bottom of the scroll activates it.
      const activateLastAtBottom = () => {
        const scrolled = scrollContainer
          ? scrollContainer
          : {
              scrollTop: window.scrollY,
              clientHeight: window.innerHeight,
              scrollHeight: document.documentElement.scrollHeight,
            };
        if (scrolled.scrollTop <= 0 || scrolled.scrollTop + scrolled.clientHeight < scrolled.scrollHeight - 2) {
          if (clampedClickRef.current?.reachedBottom) clampedClickRef.current = null;
          return false;
        }
        if (clampedClickRef.current) {
          clampedClickRef.current.reachedBottom = true;
          return true;
        }
        const last = [...sections].reverse().find((section) => resolveSectionElement(section.id));
        if (!last) return false;
        activate(last.id);
        return true;
      };

      // The last section whose top has passed the line, so a short section at the top of the screen is the
      // active one rather than the next section below it.
      const activateOnScreen = () => {
        if (activateLastAtBottom()) return;
        const line = (scrollContainer ? scrollContainer.getBoundingClientRect().top : 0) + ACTIVE_LINE_PX;
        let onScreen: string | undefined;
        for (const section of sections) {
          const element = resolveSectionElement(section.id);
          if (!element) continue;
          if (element.getBoundingClientRect().top > line) break;
          onScreen = section.id;
        }
        const id = onScreen ?? sections[0]?.id;
        if (id) activate(id);
      };

      activateOnScreen();
      const scrollTarget: HTMLElement | Window = scrollContainer ?? window;
      scrollTarget.addEventListener("scroll", activateOnScreen, { passive: true });

      return {
        disconnect: () => {
          scrollTarget.removeEventListener("scroll", activateOnScreen);
        },
      };
    };

    // Initial setup
    let tracker = checkAndObserve();

    // Re-check visibility on window resize
    const handleResize = () => {
      tracker?.disconnect();
      tracker = checkAndObserve();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      tracker?.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [
    sections,
    sectionIdPrefix,
    controlledActiveSection,
    onSectionChange,
    orientation,
    getScrollContainer,
    resolveSectionElement,
  ]);

  // Filter sections if filter function provided
  const visibleSections = React.useMemo(() => {
    return filterSection ? sections.filter(filterSection) : sections;
  }, [sections, filterSection]);

  const handleSectionClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      e.preventDefault(); // Prevent default hash navigation

      const element = resolveSectionElement(sectionId);
      if (element) {
        window.dispatchEvent(new CustomEvent(SECTION_NAVIGATE_EVENT, { detail: { id: element.id } }));
        const scrollContainer = getScrollContainer();

        if (scrollContainer) {
          // Scroll within the inner container (e.g. AppShell's #app-content).
          // Calculate the element's offset relative to the scroll container.
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const targetScroll = scrollContainer.scrollTop + (elementRect.top - containerRect.top);
          const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
          clampedClickRef.current = targetScroll > maxScrollTop ? { id: sectionId, reachedBottom: false } : null;

          scrollContainer.scrollTo({
            top: Math.max(0, Math.min(targetScroll, maxScrollTop)),
            behavior: scrollBehavior,
          });
        } else {
          // Fallback: scroll the window (for pages not inside AppShell)
          const elementTop = element.getBoundingClientRect().top + window.scrollY;
          const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          clampedClickRef.current = elementTop > maxScrollTop ? { id: sectionId, reachedBottom: false } : null;
          const targetScroll = Math.max(0, Math.min(elementTop, maxScrollTop));

          window.scrollTo({ top: targetScroll, behavior: scrollBehavior });
        }

        // Update URL hash without triggering scroll
        if (window.history.pushState) {
          window.history.pushState(null, "", `#${sectionIdPrefix}${sectionId}`);
        }

        // Update active section
        if (onSectionChange) {
          onSectionChange(sectionId);
        } else {
          setInternalActiveSection(sectionId);
        }
      }
    },
    [sectionIdPrefix, scrollBehavior, onSectionChange, getScrollContainer, resolveSectionElement]
  );

  if (visibleSections.length === 0) {
    return null;
  }

  // Determine display classes based on orientation
  const verticalClasses =
    orientation === "vertical"
      ? "" // Always show
      : orientation === "horizontal"
        ? "hidden" // Never show
        : "hidden lg:block"; // Responsive: show on desktop

  const horizontalClasses =
    orientation === "horizontal"
      ? "" // Always show
      : orientation === "vertical"
        ? "hidden" // Never show
        : "lg:hidden"; // Responsive: show on mobile

  return (
    <>
      {/* Vertical Sidebar Navigation */}
      <nav ref={verticalNavRef} className={twMerge(verticalClasses, className)} aria-label="Section navigation">
        <div className="space-y-1" style={{ position: "sticky", top: stickyTop }}>
          {heading ? (
            <div className="px-3 pb-2 text-label-sm font-semibold uppercase tracking-wide text-text-tertiary">
              {heading}
            </div>
          ) : null}
          {visibleSections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <a
                key={section.id}
                href={`#${sectionIdPrefix}${section.id}`}
                onClick={(e) => handleSectionClick(e, section.id)}
                className={twMerge(
                  "block w-full text-left px-3 py-2 rounded-md text-sm transition-colors no-underline",
                  isActive
                    ? "bg-background-muted text-text-primary font-semibold"
                    : "text-text-secondary hover:text-text-primary hover:bg-background-hover"
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="flex items-center justify-between gap-2">
                  <span>{section.label}</span>
                  {section.count !== undefined && (
                    <span className="text-xs tabular-nums text-text-secondary">{section.count}</span>
                  )}
                </span>
              </a>
            );
          })}
        </div>
      </nav>

      {/* Horizontal Tab Navigation */}
      <nav
        ref={horizontalNavRef}
        className={twMerge(horizontalClasses, "overflow-x-auto scrollbar-hide sticky top-0 z-10 py-4 -mt-4", className)}
        aria-label="Section navigation"
      >
        <div className="flex gap-2 min-w-max">
          {visibleSections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <a
                key={section.id}
                href={`#${sectionIdPrefix}${section.id}`}
                onClick={(e) => handleSectionClick(e, section.id)}
                className={twMerge(
                  "inline-block px-4 py-2 rounded-md text-sm whitespace-nowrap transition-colors no-underline",
                  isActive
                    ? "bg-background-muted text-text-primary font-semibold"
                    : "text-text-secondary hover:text-text-primary hover:bg-background-hover"
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="flex items-center gap-2">
                  <span>{section.label}</span>
                  {section.count !== undefined && (
                    <span className="text-xs tabular-nums text-text-secondary">({section.count})</span>
                  )}
                </span>
              </a>
            );
          })}
        </div>
      </nav>
    </>
  );
}
